import {simulatorActions, simulatorStates} from '../../../store';
import styles from './Controls.module.css';
import * as Simulator from '../../../utils/simulator';
import NumericInput from '../../../components/NumericInput/NumericInput';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Button,
    Slider,
    Stack,
    Tooltip, Typography
} from '@mui/material';
import {
    PlayArrow as PlayArrowIcon,
    Pause as PauseIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import {useSelector, useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import React from 'react';

const Controls = () => {
    const controllerType = useSelector(state => state.simulator.controllerType);
    const plantName = useSelector(state => state.simulator.plant);
    const plant = Simulator.plants.find(plant => plant.getName() === plantName);
    const u_man = useSelector(state => state.simulator.u_man);
    const r = useSelector(state => state.simulator.r);
    const e_lower = useSelector(state => state.simulator.e_lower);
    const e_middle1 = useSelector(state => state.simulator.e_middle1);
    const e_middle2 = useSelector(state => state.simulator.e_middle2);
    const e_upper = useSelector(state => state.simulator.e_upper);
    const u_lower = useSelector(state => state.simulator.u_lower);
    const u_middle = useSelector(state => state.simulator.u_middle);
    const u_upper = useSelector(state => state.simulator.u_upper);
    const k_p = useSelector(state => state.simulator.k_p);
    const t_i = useSelector(state => state.simulator.t_i);
    const t_d = useSelector(state => state.simulator.t_d);
    const t_f = useSelector(state => state.simulator.t_f);
    const simulatorState = useSelector(state => state.simulator.simulatorState);

    const dispatch = useDispatch();
    const {t} = useTranslation();

    const unitKp = plant.unitE ? (plant.unitU ?? '1') + '/' + plant.unitE : plant.unitU;

    return (
        <div className={styles.controls}>
            <div className={styles.SimulatorControls}>
                <FormControl
                    style={{marginTop: 10}}
                    variant="outlined"
                    fullWidth
                >
                    <InputLabel id="plant_select_label">{t('plant')}</InputLabel>
                    <Select
                        labelId="plant_select_label"
                        value={plantName}
                        onChange={event => dispatch(simulatorActions.changePlant(event.target.value))}
                        label={t('plant')}
                    >
                        {Simulator.plants.map(plant => (
                            <MenuItem
                                key={plant.getName()}
                                value={plant.getName()}
                            >
                                {t(plant.getName())}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>


                <FormControl
                    variant="outlined"
                    fullWidth
                >
                    <InputLabel id="controller_select_label">{t('controller')}</InputLabel>
                    <Select
                        value={controllerType}
                        onChange={event => dispatch(simulatorActions.changeController(event.target.value))}
                        label={t('controller')}
                    >
                        {Simulator.controllers.map(controller => (
                            <MenuItem
                                key={controller.getName()}
                                value={controller.getName()}
                            >
                                {t(controller.getName())}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {controllerType === Simulator.ManualController.getName() ?
                    <NumericInput
                        label='u_man'
                        value={u_man}
                        precision={2}
                        step={0.1}
                        unit={plant.unitU}
                        updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                            key: 'u_man',
                            value: newValue
                        }))}
                    /> : null
                }

                {[Simulator.TwoPositionController.getName(), Simulator.ThreePositionController.getName(), Simulator.PController.getName(),
                    Simulator.PIController.getName(), Simulator.PDController.getName(), Simulator.PIDController.getName()].includes(controllerType) ?
                    <NumericInput
                        label='r'
                        value={r}
                        precision={2}
                        step={1}
                        unit={plant.unitY}
                        updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                            key: 'r',
                            value: newValue
                        }))}
                    /> : null
                }

                {[Simulator.TwoPositionController.getName(), Simulator.ThreePositionController.getName()].includes(controllerType) ?
                    <React.Fragment>
                        <NumericInput
                            label='e_lower'
                            value={e_lower}
                            precision={2}
                            step={1}
                            unit={plant.unitE}
                            updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                                key: 'e_lower',
                                value: newValue
                            }))}
                        />
                        {controllerType === Simulator.ThreePositionController.getName() ?
                            <React.Fragment>
                                <NumericInput
                                    label='e_middle1'
                                    value={e_middle1}
                                    precision={2}
                                    step={1}
                                    unit={plant.unitE}
                                    updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                                        key: 'e_middle1',
                                        value: newValue
                                    }))}
                                />
                                <NumericInput
                                    label='e_middle2'
                                    value={e_middle2}
                                    precision={2}
                                    step={1}
                                    unit={plant.unitE}
                                    updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                                        key: 'e_middle2',
                                        value: newValue
                                    }))}
                                />
                            </React.Fragment> : null
                        }
                        <NumericInput
                            label='e_upper'
                            value={e_upper}
                            precision={2}
                            step={1}
                            unit={plant.unitE}
                            updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                                key: 'e_upper',
                                value: newValue
                            }))}
                        />
                        <NumericInput
                            label='u_lower'
                            value={u_lower}
                            precision={2}
                            step={1}
                            unit={plant.unitU}
                            updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                                key: 'u_lower',
                                value: newValue
                            }))}
                        />
                        {controllerType === Simulator.ThreePositionController.getName() ?
                            <NumericInput
                                label='u_middle'
                                value={u_middle}
                                precision={2}
                                step={1}
                                unit={plant.unitU}
                                updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                                    key: 'u_middle',
                                    value: newValue
                                }))}
                            /> : null
                        }
                        <NumericInput
                            label='u_upper'
                            value={u_upper}
                            precision={2}
                            step={1}
                            unit={plant.unitU}
                            updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                                key: 'u_upper',
                                value: newValue
                            }))}
                        />
                    </React.Fragment> : null
                }

                {[Simulator.PController.getName(),
                    Simulator.PIController.getName(), Simulator.PDController.getName(), Simulator.PIDController.getName()].includes(controllerType) ?
                    <NumericInput
                        label='k_p'
                        value={k_p}
                        precision={2}
                        step={0.1}
                        unit={unitKp}
                        updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                            key: 'k_p',
                            value: newValue
                        }))}
                    /> : null
                }

                {[Simulator.PIController.getName(), Simulator.PIDController.getName()].includes(controllerType) ?
                    <NumericInput
                        label={t('t_i')}
                        value={t_i}
                        precision={2}
                        step={0.1}
                        unit={(plant.unitT ? `${plant.unitT}` : 's')}
                        updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                            key: 't_i',
                            value: newValue
                        }))}
                    /> : null
                }

                {[Simulator.PDController.getName(), Simulator.PIDController.getName()].includes(controllerType) ?
                    <React.Fragment>
                        <NumericInput
                            label={t('t_d')}
                            value={t_d}
                            precision={2}
                            step={0.1}
                            unit={(plant.unitT ? `${plant.unitT}` : 's')}
                            updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                                key: 't_d',
                                value: newValue
                            }))}
                        />
                        <NumericInput
                            label='t_f'
                            value={t_f}
                            precision={2}
                            step={0.1}
                            unit={(plant.unitT ? `${plant.unitT}` : 's')}
                            updateValue={newValue => dispatch(simulatorActions.updateNumericValue({
                                key: 't_f',
                                value: newValue
                            }))}
                        />
                    </React.Fragment> : null
                }
            </div>
            <div className={styles.PlotControls}>
                <Tooltip title={t('limit_time_axis')} placement={'left'}>
                    <Stack direction={'row'} style={{width: '100%'}} spacing={2.5} alignItems={'center'}>
                        <Typography variant='b'><b>3  {(plant.unitT ? `${plant.unitT}` : 's')}</b></Typography>
                        <Slider
                            valueLabelDisplay={'auto'}
                            min={3}
                            max={20}
                            defaultValue={10}
                            onChange={event => {
                                dispatch(simulatorActions.setTimeLimit(event.target.value));
                            }}
                        />
                        <Typography variant='b'><b>20 {(plant.unitT ? `${plant.unitT}` : 's')}</b></Typography>
                    </Stack>
                </Tooltip>
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() => dispatch(simulatorActions.toggleSimulatorState())}
                    startIcon={simulatorState === simulatorStates.RUNNING ? <PauseIcon/> : <PlayArrowIcon/>}
                >
                    {simulatorState === simulatorStates.RESET ? t('start') : simulatorState === simulatorStates.RUNNING ? t('pause') : t('resume')}
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    onClick={() => dispatch(simulatorActions.resetSimulation())}
                    startIcon={<DeleteIcon/>}
                    disabled={simulatorState === simulatorStates.RESET}
                >
                    {t('reset')}
                </Button>
            </div>

        </div>
    );
};

export default Controls;
